import React, { useState, useEffect, useMemo } from 'react';
import './JobList.css'; // Reusing JobList styles
import useSortableTable from '../common/useSortableTable';
import JobForm from './JobForm';

const API_BASE_URL = '/api/documentation/search-items/';

const SearchItem = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedGroups, setExpandedGroups] = useState(new Set());
    const [isJobModalOpen, setIsJobModalOpen] = useState(false);
    const [currentJob, setCurrentJob] = useState(null);
    const [jobLoading, setJobLoading] = useState(false);

    // Fetch data based on search term
    useEffect(() => {
        const fetchItems = async () => {
            if (!searchTerm.trim()) {
                setItems([]);
                return;
            }

            setLoading(true);
            setError(null);
            try {
                // Adjusting URL with search parameter mapped to DRF SearchFilter
                const url = `${API_BASE_URL}?search=${encodeURIComponent(searchTerm)}`;
                const response = await fetch(url, {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('kk_token')}`,
                    },
                });
                if (!response.ok) {
                    throw new Error('Failed to fetch items');
                }
                const data = await response.json();
                setItems(Array.isArray(data) ? data : []);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        const debounceTimer = setTimeout(() => {
            fetchItems();
        }, 500); // 500ms debounce

        return () => clearTimeout(debounceTimer);
    }, [searchTerm]);

    // Apply sorting via useSortableTable
    const { items: sortedItems, requestSort, getSortIndicator } = useSortableTable(items, { key: 'idf_no', direction: 'ascending' });

    // Grouping items by IDF Number
    const groupedItems = useMemo(() => {
        const groups = {};
        sortedItems.forEach(item => {
            const idfName = item.idf_no || 'N/A';
            if (!groups[idfName]) {
                groups[idfName] = [];
            }
            groups[idfName].push(item);
        });
        return groups;
    }, [sortedItems]);

    const groupKeys = useMemo(() => Object.keys(groupedItems), [groupedItems]);
    const areAllGroupsExpanded = groupKeys.length > 0 && groupKeys.every((key) => expandedGroups.has(key));

    useEffect(() => {
        setExpandedGroups(prev => {
            const validKeys = new Set(groupKeys);
            return new Set([...prev].filter((key) => validKeys.has(key)));
        });
    }, [groupKeys]);

    // Handle expand/collapse logic
    const toggleGroupIndicator = (idfNo) => {
        const newExpanded = new Set(expandedGroups);
        if (newExpanded.has(idfNo)) {
            newExpanded.delete(idfNo);
        } else {
            newExpanded.add(idfNo);
        }
        setExpandedGroups(newExpanded);
    };

    const handleSortClick = (key) => {
        requestSort(key);
    };

    const handleToggleAllGroups = () => {
        if (areAllGroupsExpanded) {
            setExpandedGroups(new Set());
            return;
        }
        setExpandedGroups(new Set(groupKeys));
    };

    const handleViewJobById = async (jobId) => {
        if (!jobId) return;

        setJobLoading(true);
        try {
            const response = await fetch(`/api/documentation/jobs/${jobId}/`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('kk_token')}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to load job details');
            }

            const jobData = await response.json();
            setCurrentJob(jobData);
            setIsJobModalOpen(true);
        } catch (err) {
            setError(err.message || 'Unable to open job in view mode');
        } finally {
            setJobLoading(false);
        }
    };

    const handleJobModalClose = () => {
        setIsJobModalOpen(false);
        setCurrentJob(null);
    };

    return (
        <div className="job-list-container wrapper" style={{ margin: '20px 20px', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', background: '#fff' }}>
            <div className="controls-row">
                <h2>Search Items</h2>
                <div style={{ display: 'flex', alignItems: 'stretch', gap: '10px' }}>
                    <div className="search-box" style={{ flex: '0 0 auto' }}>
                        <input
                            type="text"
                            placeholder="Search by HS Code, Description, or Model/Specification..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                width: '350px',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                border: '1px solid #ccc',
                                boxSizing: 'border-box'
                            }}
                        />
                    </div>
                    <button
                        type="button"
                        className="add-btn"
                        onClick={handleToggleAllGroups}
                        disabled={groupKeys.length === 0}
                        style={{ margin: 0, alignSelf: 'stretch' }}
                    >
                        {areAllGroupsExpanded ? 'Collapse All' : 'Expand All'}
                    </button>
                </div>
            </div>

            {loading && <div className="loading">Loading items...</div>}
            {jobLoading && <div className="loading">Loading job details...</div>}
            {error && <div className="error-message">Error: {error}</div>}

            <div className="table-responsive" style={{ marginTop: '8px' }}>
                <table className="job-table compact-table stylish-table">
                    <thead>
                        <tr>
                            <th onClick={() => handleSortClick('idf_no')} style={{ cursor: 'pointer' }}>IDF No {getSortIndicator('idf_no')}</th>
                            <th onClick={() => handleSortClick('customer_name')} style={{ cursor: 'pointer' }}>Customer {getSortIndicator('customer_name')}</th>
                            <th onClick={() => handleSortClick('hs_code')} style={{ cursor: 'pointer' }}>Commodity HS Code {getSortIndicator('hs_code')}</th>
                            <th onClick={() => handleSortClick('description')} style={{ cursor: 'pointer' }}>Description {getSortIndicator('description')}</th>
                            <th onClick={() => handleSortClick('model_specification')} style={{ cursor: 'pointer' }}>Model/Specification {getSortIndicator('model_specification')}</th>
                            <th onClick={() => handleSortClick('component')} style={{ cursor: 'pointer' }}>Component {getSortIndicator('component')}</th>
                            <th onClick={() => handleSortClick('quantity')} style={{ cursor: 'pointer' }}>Quantity {getSortIndicator('quantity')}</th>
                            <th onClick={() => handleSortClick('quantity_unit')} style={{ cursor: 'pointer' }}>Units {getSortIndicator('quantity_unit')}</th>
                            <th onClick={() => handleSortClick('unit_price')} style={{ cursor: 'pointer' }}>Unit Price {getSortIndicator('unit_price')}</th>
                            <th onClick={() => handleSortClick('item_inv_price')} style={{ cursor: 'pointer' }}>Item Invoice Price {getSortIndicator('item_inv_price')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Object.keys(groupedItems).length === 0 && !loading && (
                            <tr>
                                <td colSpan="10" className="no-data">No items found matching your search.</td>
                            </tr>
                        )}
                        {Object.entries(groupedItems).map(([idfNo, groupItems]) => {
                            const hsCodes = [...new Set(groupItems
                                .map(item => (item.hs_code || '').trim())
                                .filter(Boolean)
                            )];
                            // Get customer name from first item (use short_name if available, else name)
                            const customerName = (groupItems[0]?.customer_short_name || groupItems[0]?.customer_name || 'UNKNOWN').toUpperCase();

                            return (
                            <React.Fragment key={idfNo}>
                                {/* Group Header Row */}
                                <tr 
                                    className="group-header-row" 
                                    onClick={() => toggleGroupIndicator(idfNo)} 
                                    style={{ cursor: 'pointer', backgroundColor: '#f9f9fc', fontWeight: 'bold' }}
                                >
                                    <td colSpan="10">
                                        <i className={`fas fa-chevron-${expandedGroups.has(idfNo) ? 'down' : 'right'}`} style={{ marginRight: '8px' }}></i>
                                        IDF No:{' '}
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const jobId = groupItems[0]?.job_id;
                                                handleViewJobById(jobId);
                                            }}
                                            style={{
                                                border: 'none',
                                                padding: 0,
                                                background: 'transparent',
                                                color: '#0056b3',
                                                textDecoration: 'underline',
                                                cursor: 'pointer',
                                                font: 'inherit',
                                                fontWeight: 'bold'
                                            }}
                                            title="Open job in view-only mode"
                                        >
                                            {idfNo}
                                        </button>
                                        {' '}(Count: {groupItems.length}) | CUSTOMER: {customerName}
                                        {hsCodes.length > 0 && ` | HS CODES: ${hsCodes.join(', ')}`}
                                    </td>
                                </tr>
                                {/* Group Item Rows */}
                                {expandedGroups.has(idfNo) && groupItems.map(item => (
                                    <tr key={item.id} className="item-row" style={{ backgroundColor: '#ffffff' }}>
                                        <td>
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleViewJobById(item.job_id);
                                                }}
                                                style={{
                                                    border: 'none',
                                                    padding: 0,
                                                    background: 'transparent',
                                                    color: '#0056b3',
                                                    textDecoration: 'underline',
                                                    cursor: 'pointer',
                                                    font: 'inherit'
                                                }}
                                                title="Open job in view-only mode"
                                            >
                                                {item.idf_no || 'N/A'}
                                            </button>
                                        </td>
                                        <td>{(item.customer_short_name || item.customer_name || 'N/A').toUpperCase()}</td>
                                        <td>{item.hs_code || 'N/A'}</td>
                                        <td>{item.description || 'N/A'}</td>
                                        <td>{item.model_specification || 'N/A'}</td>
                                        <td>{item.component || 'N/A'}</td>
                                        <td>{item.quantity || 0}</td>
                                        <td>{item.quantity_unit || 'N/A'}</td>
                                        <td>{parseFloat(item.unit_price || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                        <td>{parseFloat(item.item_inv_price || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                    </tr>
                                ))}
                            </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {isJobModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <button className="modal-close-btn" onClick={handleJobModalClose}>
                            &times;
                        </button>
                        <JobForm
                            mode="view"
                            initialData={currentJob}
                            onClose={handleJobModalClose}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchItem;
